import { DOCUMENT, isPlatformBrowser } from "@angular/common";
import { DestroyRef, inject, Injectable, PLATFORM_ID } from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { BehaviorSubject, finalize, timeout } from "rxjs";
import { MarketingApiService } from "./marketing-api.service";
import { MarketingMeasurementSession } from "./marketing.models";

/** Backend-authorized technical session, never a visitor preference or SDK consent. */
@Injectable({ providedIn: "root" })
export class MarketingMeasurementService {
  private readonly api = inject(MarketingApiService);
  private readonly document = inject(DOCUMENT);
  private readonly browser = isPlatformBrowser(inject(PLATFORM_ID));
  private readonly destroyRef = inject(DestroyRef);
  private readonly storageKey = "yes7-marketing-measurement";
  readonly changes = new BehaviorSubject<MarketingMeasurementSession | null>(
    null,
  );
  private busy = false;
  private requestGeneration = 0;
  private destroyed = false;
  get pending() {
    return this.busy;
  }
  get analytics() {
    return this.changes.value?.analytics_enabled === true;
  }
  get advertising() {
    return this.changes.value?.advertising_enabled === true;
  }
  get trackingReceipt() {
    return this.changes.value?.measurement_receipt;
  }
  constructor() {
    this.destroyRef.onDestroy(() => {
      this.destroyed = true;
    });
    this.ensure();
  }
  ensure(): void {
    if (!this.browser || this.destroyed || this.busy || this.trackingReceipt)
      return;
    let previous: MarketingMeasurementSession | null = null;
    try {
      previous = this.validate(
        JSON.parse(
          this.document.defaultView?.localStorage.getItem(this.storageKey) ||
            "null",
        ),
      );
    } catch {
      /* Storage is optional. */
    }
    this.request(
      previous
        ? { previous_measurement_receipt: previous.measurement_receipt }
        : {},
      false,
    );
  }
  private request(
    body: { previous_measurement_receipt?: string },
    recovered: boolean,
  ): void {
    this.busy = true;
    const generation = ++this.requestGeneration;
    this.api
      .measurementSession(body)
      .pipe(
        timeout(10000),
        takeUntilDestroyed(this.destroyRef),
        finalize(() => {
          if (generation === this.requestGeneration) this.busy = false;
        }),
      )
      .subscribe({
        next: (response) => {
          const session = this.validate(response);
          if (this.destroyed || generation !== this.requestGeneration) return;
          if (
            !session ||
            (body.previous_measurement_receipt &&
              session.measurement_receipt !== body.previous_measurement_receipt)
          ) {
            this.changes.next(null);
            return;
          }
          try {
            this.document.defaultView?.localStorage.setItem(
              this.storageKey,
              JSON.stringify(session),
            );
          } catch {
            /* Checkout remains available. */
          }
          this.changes.next(session);
        },
        error: (error: { status?: number; error?: { code?: string } }) => {
          if (this.destroyed || generation !== this.requestGeneration) return;
          if (
            !recovered &&
            body.previous_measurement_receipt &&
            error.status === 422 &&
            error.error?.code === "measurement_receipt_invalid"
          ) {
            try {
              this.document.defaultView?.localStorage.removeItem(
                this.storageKey,
              );
            } catch {
              /* Stored receipt remains untrusted. */
            }
            this.request({}, true);
            return;
          }
          this.changes.next(null);
        },
      });
  }
  private validate(value: unknown): MarketingMeasurementSession | null {
    if (!value || typeof value !== "object" || Array.isArray(value))
      return null;
    const session = value as Partial<MarketingMeasurementSession>;
    if (
      typeof session.measurement_receipt !== "string" ||
      !/^[A-Za-z0-9_-]{1,128}$/.test(session.measurement_receipt) ||
      session.policy_version !== "platform_measurement_v1" ||
      session.analytics_enabled !== true ||
      session.advertising_enabled !== true
    )
      return null;
    return {
      measurement_receipt: session.measurement_receipt,
      policy_version: "platform_measurement_v1",
      analytics_enabled: true,
      advertising_enabled: true,
    };
  }
}
